// Escolher documentos para a conversa: botão no campo de mensagem + popover acima dele, multisseleção (até `max`) com busca por nome.
// Setas navegam, Enter marca/desmarca, Esc fecha. A seleção é de quem chama (`selected`); aqui só a lista (GET /api/client/documents) e a busca.
import { Fragment, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router';
import { Check, FileText, Search } from 'lucide-react';
import { api, type Doc } from '../api';
import { fold } from '../models/catalog';
import '../styles/doc-picker.css';

export const LABEL = 'Escolher documentos para esta conversa';

export function DocPicker({ selected, onChange, max }: { selected: Doc[]; onChange: (docs: Doc[]) => void; max: number }) {
  const uid = useId();
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState<Doc[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const gen = useRef(0);   // a resposta de uma listagem que já foi trocada por outra (ou fechada) não mexe na tela

  const load = () => {
    const mine = ++gen.current;
    setError(null); setDocs(null);
    api<Doc[]>('/api/client/documents').then((l) => { if (mine === gen.current) setDocs(l.filter((d) => d.chunkCount > 0)); }).catch((e) => { if (mine === gen.current) setError((e as Error).message); });
  };
  const show = () => { setQuery(''); setActive(0); setOpen(true); load(); };
  const dismiss = () => { gen.current++; setOpen(false); };
  const close = () => { dismiss(); triggerRef.current?.focus(); };

  // clique fora do popover fecha (sem roubar o foco do que foi clicado)
  useEffect(() => {
    if (!open) return;
    const down = (e: MouseEvent) => {
      const n = e.target as Node;
      if (!popRef.current?.contains(n) && !triggerRef.current?.contains(n)) dismiss();
    };
    document.addEventListener('mousedown', down);
    return () => document.removeEventListener('mousedown', down);
  }, [open]);

  // próprios primeiro, depois os compartilhados comigo (conta de empresa); a ordem de cada grupo é a do servidor
  const shown = useMemo(() => { const q = fold(query.trim()); return (docs ?? []).filter((d) => !q || fold(d.name).includes(q)).sort((a, b) => Number(a.access === 'shared') - Number(b.access === 'shared')); }, [docs, query]);
  const grouped = shown.some((d) => d.access === 'shared');
  const isOn = (d: Doc) => selected.some((x) => x.id === d.id);
  const full = selected.length >= max;
  const toggle = (d: Doc) => {
    if (isOn(d)) onChange(selected.filter((x) => x.id !== d.id));
    else if (!full) onChange([...selected, d]);
  };

  useEffect(() => { if (open) popRef.current?.focus(); }, [open]);   // sem campo de busca ainda (carregando), o Esc precisa de um foco dentro do popover
  useEffect(() => { listRef.current?.querySelector<HTMLElement>(`[data-i="${active}"]`)?.scrollIntoView({ block: 'nearest' }); }, [active, shown]);

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); return; }
    if (!shown.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(shown.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') { e.preventDefault(); toggle(shown[active]); }
  };

  const hasDocs = docs !== null && docs.length > 0;
  return (
    <>
      <button type="button" className={`dp-btn${selected.length ? ' has' : ''}`} ref={triggerRef} aria-label={LABEL} title={LABEL} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? `${uid}-pop` : undefined} onClick={() => (open ? close() : show())}>
        <FileText />
        {selected.length > 0 && <span className="dp-count" aria-hidden="true">{selected.length}</span>}
      </button>

      {open && (
        <div className="dp-pop" id={`${uid}-pop`} ref={popRef} tabIndex={-1} role="dialog" aria-label="Documentos da conversa" onKeyDown={onKey}>
          {hasDocs && (
            <div className="dp-search">
              <Search />
              <input
                autoFocus type="text" value={query} onChange={(e) => { setQuery(e.target.value); setActive(0); }}
                placeholder="Buscar documento…" aria-label="Buscar documento" autoComplete="off" spellCheck={false}
                role="combobox" aria-expanded="true" aria-controls={`${uid}-list`} aria-activedescendant={shown[active] ? `${uid}-o${active}` : undefined}
              />
            </div>
          )}

          <div className="dp-body" ref={listRef}>
            {error ? (
              <div className="dp-state"><b>Não foi possível carregar</b><span>{error}</span><button type="button" className="btn sm" autoFocus onClick={load}>Tentar de novo</button></div>
            ) : docs === null ? (
              <div className="dp-skel" aria-busy="true" aria-label="Carregando documentos"><div className="skel" /><div className="skel" /><div className="skel" /></div>
            ) : docs.length === 0 ? (
              <div className="dp-state"><b>Nenhum documento</b><Link className="dp-link" to="/documentos" autoFocus>Enviar em Documentos</Link></div>
            ) : shown.length === 0 ? (
              <div className="dp-state"><b>Nenhum documento encontrado</b><span>Nada corresponde a “{query.trim()}”.</span></div>
            ) : (
              <div id={`${uid}-list`} role="listbox" aria-multiselectable="true" aria-label="Seus documentos">
                {shown.map((d, i) => {
                  const on = isOn(d), blocked = !on && full;
                  return (
                    <Fragment key={d.id}>
                    {grouped && (i === 0 || (shown[i - 1].access === 'shared') !== (d.access === 'shared')) && <div className="dp-grp" role="presentation">{d.access === 'shared' ? 'Compartilhados comigo' : 'Meus'}</div>}
                    <div
                      id={`${uid}-o${i}`} data-i={i} role="option" aria-selected={on} aria-disabled={blocked || undefined}
                      className={`dp-opt${i === active ? ' active' : ''}${on ? ' on' : ''}${blocked ? ' off' : ''}`}
                      onMouseMove={() => { if (active !== i) setActive(i); }} onClick={() => toggle(d)}
                    >
                      <span className="dp-box" aria-hidden="true">{on && <Check />}</span>
                      <span className="dp-name" title={d.name}>{d.name}</span>
                      <span className="dp-n">{d.chunkCount} {d.chunkCount === 1 ? 'trecho' : 'trechos'}</span>
                    </div>
                    </Fragment>
                  );
                })}
              </div>
            )}
          </div>

          {hasDocs && (
            <div className="dp-foot">
              <span role="status">{full ? `máximo de ${max} documentos` : `${selected.length} de ${max}`}</span>
              <button type="button" className="btn sm" onClick={close}>Concluir</button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
